import {useEffect, useState} from "react";
import {NavLink} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    Blockquote, Breadcrumb, BreadcrumbHome, BreadcrumbItem,
    Button, Modal, Text, TextArea, FormGroup, Loading
} from "@wfp/ui";
import {DiscardRequest} from "../models/types";
import {getDiscardRequests, approveDiscardRequest, cancelDiscardRequest, rejectDiscardRequest} from "../restapi/discard-request";
import {StatusBadge} from "../components/commons/status-badge";
import {datetimeParse} from "../hooks/formats";
import {LoadingCenter} from "../components/commons/loading-center";

const STATUS_LABELS: Record<number, string> = {
    0: 'PENDING',
    10: 'APPROVED',
    20: 'REJECTED',
    30: 'CANCELLED',
};

export default function DiscardRequests() {
    const {t} = useTranslation();

    const [results, setResults] = useState<DiscardRequest[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState<string | null>(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    // Approve modal
    const [approveTarget, setApproveTarget] = useState<DiscardRequest | null>(null);
    const [approveNotes, setApproveNotes] = useState('');

    // Reject modal
    const [rejectTarget, setRejectTarget] = useState<DiscardRequest | null>(null);
    const [rejectNotes, setRejectNotes] = useState('');
    const [rejectNotesError, setRejectNotesError] = useState<string | null>(null);

    // Cancel modal
    const [cancelTarget, setCancelTarget] = useState<DiscardRequest | null>(null);

    useEffect(() => {
        loadPage(1, true);
    }, []);

    function loadPage(pageNumber: number, reset = false) {
        setLoading(true);
        setError(null);
        getDiscardRequests(pageNumber)
            .then(page => {
                setResults(prev => reset ? page.results : [...prev, ...page.results]);
                setTotalCount(page.count);
                setNextPage(page.next);
                setCurrentPageNumber(pageNumber);
            })
            .catch(() => setError(t('error_loading')))
            .finally(() => setLoading(false));
    }

    function reload() {
        loadPage(1, true);
    }

    function handleLoadMore() {
        loadPage(currentPageNumber + 1, false);
    }

    function handleApproveOpen(req: DiscardRequest) {
        setApproveTarget(req);
        setApproveNotes('');
        setActionError(null);
    }

    function handleApproveConfirm() {
        if (!approveTarget) return;
        setActionLoading(true);
        setActionError(null);
        approveDiscardRequest(approveTarget.id, approveNotes)
            .then(() => {
                setApproveTarget(null);
                reload();
            })
            .catch(e => setActionError(e?.response?.data?.detail || t('error_generic')))
            .finally(() => setActionLoading(false));
    }

    function handleRejectOpen(req: DiscardRequest) {
        setRejectTarget(req);
        setRejectNotes('');
        setRejectNotesError(null);
        setActionError(null);
    }

    function handleRejectConfirm() {
        if (!rejectTarget) return;
        if (!rejectNotes.trim()) {
            setRejectNotesError(t('review_notes_required'));
            return;
        }
        setActionLoading(true);
        setActionError(null);
        rejectDiscardRequest(rejectTarget.id, rejectNotes)
            .then(() => {
                setRejectTarget(null);
                reload();
            })
            .catch(e => setActionError(e?.response?.data?.detail || t('error_generic')))
            .finally(() => setActionLoading(false));
    }

    function handleCancel(req: DiscardRequest) {
        setCancelTarget(req);
        setActionError(null);
    }

    function handleCancelConfirm() {
        if (!cancelTarget) return;
        setActionLoading(true);
        setActionError(null);
        cancelDiscardRequest(cancelTarget.id)
            .then(() => {
                setCancelTarget(null);
                reload();
            })
            .catch(e => setActionError(e?.response?.data?.detail || t('error_generic')))
            .finally(() => setActionLoading(false));
    }

    return (
        <>
            <div className="m-10">
                <Breadcrumb className="mb-5">
                    <BreadcrumbItem>
                        <NavLink to="/"><BreadcrumbHome/></NavLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem disableLink>
                        {t('discard_requests')}
                    </BreadcrumbItem>
                </Breadcrumb>

                <Text kind="h1">{t('discard_requests')}</Text>

                {actionError && (
                    <Blockquote kind="error" withIcon className="mt-4">{actionError}</Blockquote>
                )}

                {loading && results.length === 0 && <LoadingCenter/>}

                {!!error && (
                    <Blockquote kind="error" withIcon>{error}</Blockquote>
                )}

                {!loading && results.length === 0 && !error && (
                    <Blockquote kind="info" withIcon className="mt-4">
                        {t('no_discard_requests')}
                    </Blockquote>
                )}

                {results.length > 0 && (
                    <>
                        <table className="w-full mt-5 border-collapse text-sm">
                            <thead>
                                <tr className="border-b bg-gray-50">
                                    <th className="text-left p-3">{t('id')}</th>
                                    <th className="text-left p-3">{t('target')}</th>
                                    <th className="text-left p-3">{t('state')}</th>
                                    <th className="text-left p-3">{t('discard_reason')}</th>
                                    <th className="text-left p-3">{t('notes')}</th>
                                    <th className="text-left p-3">{t('requested_by')}</th>
                                    <th className="text-left p-3">{t('created_on')}</th>
                                    <th className="text-left p-3">{t('status')}</th>
                                    <th className="text-left p-3">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((req) => (
                                    <tr key={req.id} className="border-b hover:bg-gray-50">
                                        <td className="p-3">#{req.id}</td>
                                        <td className="p-3">{req.targetDisplay}</td>
                                        <td className="p-3">
                                            {req.targetState
                                                ? <StatusBadge status={req.targetState}/>
                                                : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="p-3">{req.discardReasonNameEn}</td>
                                        <td className="p-3">{req.notes || <span className="text-gray-400">—</span>}</td>
                                        <td className="p-3">{req.requesterName}</td>
                                        <td className="p-3">{datetimeParse(req.createdOn)}</td>
                                        <td className="p-3">
                                            <StatusBadge status={STATUS_LABELS[req.status] || req.statusDisplay}/>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex gap-2">
                                                {req.canApprove && (
                                                    <>
                                                        <Button
                                                            small
                                                            kind="primary"
                                                            disabled={actionLoading}
                                                            onClick={() => handleApproveOpen(req)}
                                                        >
                                                            {t('approve')}
                                                        </Button>
                                                        <Button
                                                            small
                                                            kind="danger"
                                                            disabled={actionLoading}
                                                            onClick={() => handleRejectOpen(req)}
                                                        >
                                                            {t('reject')}
                                                        </Button>
                                                    </>
                                                )}
                                                {req.canCancel && (
                                                    <Button
                                                        small
                                                        kind="ghost"
                                                        disabled={actionLoading}
                                                        onClick={() => handleCancel(req)}
                                                    >
                                                        {t('cancel')}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                            <span>{t('showing_of', {shown: results.length, total: totalCount})}</span>
                            {nextPage && (
                                <Button
                                    kind="secondary"
                                    small
                                    disabled={loading}
                                    onClick={handleLoadMore}
                                >
                                    {loading ? <Loading small withOverlay={false}/> : t('load_more')}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Approve Modal */}
            <Modal
                open={!!approveTarget}
                modalHeading={t('approve_discard_request') as string}
                primaryButtonText={t('approve') as string}
                secondaryButtonText={t('cancel') as string}
                onRequestSubmit={handleApproveConfirm}
                onRequestClose={() => setApproveTarget(null)}
                onSecondarySubmit={() => setApproveTarget(null)}
                primaryButtonDisabled={actionLoading}
            >
                {approveTarget && (
                    <div>
                        <p className="mb-4">{t('approve_discard_request_confirm', {target: approveTarget.targetDisplay})}</p>
                        <FormGroup legendText="">
                            <TextArea
                                id="approve-notes"
                                labelText={t('review_notes_optional') as string}
                                value={approveNotes}
                                onChange={(e: any) => setApproveNotes(e.target.value)}
                                disabled={actionLoading}
                                rows={3}
                            />
                        </FormGroup>
                        {actionLoading && <Loading withOverlay={false}/>}
                        {actionError && <Blockquote kind="error" withIcon className="mt-2">{actionError}</Blockquote>}
                    </div>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal
                open={!!rejectTarget}
                modalHeading={t('reject_discard_request') as string}
                primaryButtonText={t('reject') as string}
                secondaryButtonText={t('cancel') as string}
                onRequestSubmit={handleRejectConfirm}
                onRequestClose={() => setRejectTarget(null)}
                onSecondarySubmit={() => setRejectTarget(null)}
                primaryButtonDisabled={actionLoading}
                danger
            >
                {rejectTarget && (
                    <div>
                        <p className="mb-4">{t('reject_discard_request_confirm', {target: rejectTarget.targetDisplay})}</p>
                        <FormGroup legendText="">
                            <TextArea
                                id="reject-notes"
                                labelText={t('review_notes') as string}
                                value={rejectNotes}
                                onChange={(e: any) => {
                                    setRejectNotes(e.target.value);
                                    setRejectNotesError(null);
                                }}
                                disabled={actionLoading}
                                rows={3}
                                invalid={!!rejectNotesError}
                                invalidText={rejectNotesError || undefined}
                            />
                        </FormGroup>
                        {actionLoading && <Loading withOverlay={false}/>}
                        {actionError && <Blockquote kind="error" withIcon className="mt-2">{actionError}</Blockquote>}
                    </div>
                )}
            </Modal>

            {/* Cancel Modal */}
            <Modal
                open={!!cancelTarget}
                modalHeading={t('cancel_discard_request') as string}
                primaryButtonText={t('confirm') as string}
                secondaryButtonText={t('cancel') as string}
                onRequestSubmit={handleCancelConfirm}
                onRequestClose={() => setCancelTarget(null)}
                onSecondarySubmit={() => setCancelTarget(null)}
                primaryButtonDisabled={actionLoading}
                danger
            >
                {cancelTarget && (
                    <div>
                        <p>{t('confirm_cancel_discard_request')}</p>
                        {actionLoading && <Loading withOverlay={false}/>}
                        {actionError && <Blockquote kind="error" withIcon className="mt-2">{actionError}</Blockquote>}
                    </div>
                )}
            </Modal>
        </>
    );
}
